import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import { DropDownList, DropDownListController } from '@components-clean/core/drop-down-list';
import { GroupedDropdownListController } from '@components-clean/core/grouped-dropdown-list';

interface DropDownListPageProps {
    isDisabled: boolean;
}

interface DropDownListOption {
    id: number;
    value: string;
    groupedBy?: string;
    disabled?: boolean;
}

const data: DropDownListOption[] = [
    'Pizza',
    'Burger',
    'Pasta',
    'Burrito',
    'Cupcake',
    'Cookies',
    'Eggs',
    'Bread',
    'Ham',
    'Dust',
].map((food, index) => ({
    id: index + 1,
    value: food,
    active: index !== 0,
}));

const groupedData: DropDownListOption[] = [
    {
        id: 1,
        value: 'Pizza',
        groupedBy: 'Dishes',
    },
    {
        id: 2,
        value: 'Burger',
        groupedBy: 'Dishes',
        disabled: true,
    },
    {
        id: 3,
        value: 'Pasta',
        groupedBy: 'Dishes',
    },
    {
        id: 4,
        value: 'Burrito',
        groupedBy: 'Dishes',
    },
    {
        id: 9,
        value: 'Eggs',
        groupedBy: 'Ingredients',
    },
    {
        id: 10,
        value: 'Ham',
        groupedBy: 'Ingredients',
        disabled: true,
    },
    {
        id: 11,
        value: 'Dust',
        groupedBy: 'Ingredients',
    },
    {
        id: 12,
        value: 'Milk',
        groupedBy: 'Ingredients',
    },
    {
        id: 5,
        value: 'Cupcake',
        groupedBy: 'Baked',
    },
    {
        id: 6,
        value: 'Cookies',
        groupedBy: 'Baked',
    },
    {
        id: 7,
        value: 'Bread',
        groupedBy: 'Baked',
    },
    {
        id: 8,
        value: 'Bun',
        groupedBy: 'Baked',
    },
];

type DropDownListForm = {
    dropdownSolid: DropDownListOption | null;
    dropdownSolidRequired: DropDownListOption | null;
    dropdownSolidPizzaInvalid: DropDownListOption | null;
    dropdownSolidReadonly: DropDownListOption | null;
    dropdownSolidInactive: DropDownListOption | null;
    dropdownGrouped: DropDownListOption | null;
    dropdownFlat: DropDownListOption | null;
    dropdownFlatRequired: DropDownListOption | null;
    dropdownFlatPizzaInvalid: DropDownListOption | null;
    dropdownFlatReadonly: DropDownListOption | null;
    dropdownFlatInactive: DropDownListOption | null;
};

const DropDownListPage = ({ isDisabled }: DropDownListPageProps) => {
    // React Hook Form
    const { control, handleSubmit } = useForm<DropDownListForm>({
        defaultValues: {
            dropdownSolid: null,
            dropdownSolidRequired: null,
            dropdownSolidPizzaInvalid: null,
            dropdownGrouped: null,
            dropdownFlat: null,
            dropdownFlatRequired: null,
            dropdownFlatPizzaInvalid: null,
            dropdownFlatReadonly: null,
        },
    });

    const onSubmit: SubmitHandler<DropDownListForm> = (dataHookForm) => {
        // eslint-disable-next-line no-alert
        alert(JSON.stringify(dataHookForm, null, 2));
    };
    const [readonly, setReadonly] = useState<boolean>(true);

    const [dropdownSolidReadOnly, setDropdownSolidReadOnly] = useState<DropDownListOption | null>(data[0]);
    const [dropdownFlatReadOnly, setDropdownFlatReadOnly] = useState<DropDownListOption | null>(data[0]);

    return (
        <div className="k-gap-4" style={{ minWidth: 150, maxWidth: 350 }}>
            <form onSubmit={handleSubmit(onSubmit)} className="k-d-flex k-d-flex-col k-gap-4" noValidate>
                <h2 className="k-mb-0 !k-mt-0">Dropdown Lists</h2>

                {/* Fill Mode = "Solid" (default) */}
                <DropDownListController
                    control={control}
                    name="dropdownSolid"
                    data={data}
                    data-testid="dropdownSolid"
                    label="Dropdown Solid - (Optional)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                />

                <DropDownListController
                    control={control}
                    name="dropdownSolidRequired"
                    data={data}
                    data-testid="dropdownSolidRequired"
                    label="Dropdown Solid - (Required)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    required
                />

                <DropDownListController
                    control={control}
                    name="dropdownSolidPizzaInvalid"
                    data={data}
                    data-testid="dropdownSolidPizzaInvalid"
                    label="Dropdown Solid - ('Pizza' is invalid)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    validateRules={{
                        validatePizza: (value) => (value?.value === 'Pizza' ? 'Pizza is out of stock' : true),
                    }}
                />

                <GroupedDropdownListController
                    control={control}
                    name="dropdownGrouped"
                    data={groupedData}
                    data-testid="dropdownGrouped"
                    groupField="groupedBy"
                    label="Dropdown Grouped"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                />

                <DropDownList
                    data={[]}
                    data-testid="dropdownSolidDisabled"
                    label="Dropdown Solid - (Disabled)"
                    disabled
                />

                <DropDownList
                    data={data}
                    textField="value"
                    data-testid="dropdownReadonly"
                    label="Dropdown - (readonly)"
                    readOnly
                />

                <DropDownList
                    data={data}
                    textField="value"
                    data-testid="dropdownVariant"
                    label='Dropdown - ("card-header" variant)'
                    type="card-header"
                />

                <DropDownListController
                    control={control}
                    name="dropdownSolidInactive"
                    data={data}
                    value={data[0]}
                    data-testid="dropdownSolidInactiveControlled"
                    label="Dropdown Solid - Inactive"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    activeField="active"
                    showInactiveIconInList
                />

                {/* Fill Mode = "Flat" */}
                <DropDownListController
                    control={control}
                    name="dropdownFlat"
                    data={data}
                    data-testid="dropdownFlat"
                    label="Dropdown Flat - (Optional)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    fillMode="flat"
                />

                <DropDownListController
                    control={control}
                    name="dropdownFlatRequired"
                    required
                    fillMode="flat"
                    data={data}
                    data-testid="dropdownFlatRequired"
                    label="Dropdown Flat - (Required)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                />

                <DropDownListController
                    control={control}
                    name="dropdownFlatPizzaInvalid"
                    fillMode="flat"
                    data={data}
                    data-testid="dropdownFlatPizzaInvalid"
                    label="Dropdown Flat - ('Pizza' is invalid)"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    validateRules={{
                        validatePizza: (value) => (value?.value === 'Pizza' ? 'Pizza is out of stock' : true),
                    }}
                />

                <DropDownList
                    fillMode="flat"
                    data={[]}
                    data-testid="dropdownFlatDisabled"
                    label="Dropdown Flat - (Disabled)"
                    disabled
                />

                <DropDownList
                    fillMode="flat"
                    data={data}
                    textField="value"
                    value={data[0]}
                    data-testid="dropdownFlatReadonly"
                    label="Dropdown Flat - (readonly)"
                    readOnly
                />

                <DropDownListController
                    control={control}
                    fillMode="flat"
                    name="dropdownFlatInactive"
                    data={data}
                    value={data[0]}
                    data-testid="dropdownFlatInactiveControlled"
                    label="Dropdown Flat - Inactive"
                    disabled={isDisabled}
                    textField="value"
                    dataItemKey="id"
                    activeField="active"
                    showInactiveIconInList
                />

                <Card
                    data-testid="dropdownReadonlyCard"
                    headerActions={
                        <Button data-testid="toggleReadonly" onClick={() => setReadonly(!readonly)}>
                            Toggle Readonly{' '}
                        </Button>
                    }
                >
                    <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                        <DropDownList
                            data={data}
                            value={dropdownSolidReadOnly}
                            onChange={(e) => setDropdownSolidReadOnly(e.value as DropDownListOption | null)}
                            textField="value"
                            data-testid="dropdownSolidReadonly"
                            label="Dropdown Solid - (readonly)"
                            readOnly={readonly}
                            activeField="active"
                        />
                        <DropDownListController
                            control={control}
                            name="dropdownSolidReadonly"
                            data={data}
                            data-testid="dropdownSolidReadonlyControlled"
                            label="Dropdown Solid - (Readonly Controlled)"
                            disabled={isDisabled}
                            textField="value"
                            dataItemKey="id"
                            readOnly={readonly}
                        />

                        <DropDownList
                            fillMode="flat"
                            data={data}
                            value={dropdownFlatReadOnly}
                            onChange={(e) => setDropdownFlatReadOnly(e.value as DropDownListOption | null)}
                            textField="value"
                            data-testid="dropdownFlatReadonly"
                            label="Dropdown Flat - (readonly)"
                            readOnly={readonly}
                            activeField="active"
                        />

                        <DropDownListController
                            control={control}
                            name="dropdownFlatReadonly"
                            data={data}
                            data-testid="dropdownFlatReadonlyControlled"
                            label="Dropdown Flat - (Readonly Controlled)"
                            disabled={isDisabled}
                            textField="value"
                            dataItemKey="id"
                            fillMode="flat"
                            readOnly={readonly}
                        />
                    </div>
                </Card>

                <Button type="submit" data-testid="submit-button" primary disabled={isDisabled}>
                    Submit
                </Button>
            </form>
        </div>
    );
};

export default DropDownListPage;
